import { useEffect, useRef, useState } from "react";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Upload, Loader2, ScanLine, CheckCircle2, Camera } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";

export default function ReceiptScanDialog({ open, onOpenChange, onCreated }) {
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState(null);
  const [data, setData] = useState(null);
  const fileRef = useRef(null);
  const cameraRef = useRef(null);

  useEffect(() => {
    if (!open) {
      setBusy(false);
      setPreview(null);
      setData(null);
    }
  }, [open]);

  const onPick = async (file) => {
    if (!file) return;
    setPreview(URL.createObjectURL(file));
    setBusy(true);
    let extracted = null;
    try {
      const fd = new FormData();
      fd.append("image", file, file.name);
      const res = await api.post("/ai/receipt", fd, { headers: { "Content-Type": "multipart/form-data" } });
      extracted = res.data;
      setData(extracted);
    } catch (e) {
      toast.error(formatApiError(e, "Receipt scan failed"));
    } finally {
      setBusy(false);
      if (extracted && !extracted.amount && !extracted.merchant) {
        toast.message("Couldn't read this receipt — try a clearer photo, or fill manually.");
      }
    }
  };

  const update = (k, v) => setData((p) => ({ ...(p || {}), [k]: v }));

  const save = async () => {
    if (!data?.amount) {
      toast.error("Amount missing");
      return;
    }
    try {
      await api.post("/expenses", {
        amount: Number(data.amount),
        category: data.category || "Other",
        payment_method: data.payment_method || "Card",
        date: data.date || new Date().toISOString().slice(0, 10),
        notes: data.merchant ? `${data.merchant}${data.notes ? " — " + data.notes : ""}` : (data.notes || "Receipt"),
      });
      toast.success("Receipt saved");
      onCreated?.();
      onOpenChange(false);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg" data-testid="receipt-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">Scan receipt</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {!preview ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <button
                type="button"
                onClick={() => cameraRef.current?.click()}
                className="flex flex-col items-center justify-center gap-2 rounded-xl border-2 border-dashed border-primary/50 bg-primary/5 py-8 transition-all hover:-translate-y-0.5 hover:border-primary hover:bg-primary/10"
                data-testid="receipt-camera-button"
              >
                <Camera className="h-9 w-9 text-primary" strokeWidth={1.6} />
                <div className="font-medium">Take photo</div>
                <div className="text-xs text-muted-foreground">Recommended on mobile</div>
              </button>
              <button
                type="button"
                onClick={() => fileRef.current?.click()}
                className="flex flex-col items-center justify-center gap-2 rounded-xl border-2 border-dashed border-border bg-accent/20 py-8 transition-all hover:-translate-y-0.5 hover:border-primary"
                data-testid="receipt-pick-button"
              >
                <ScanLine className="h-9 w-9 text-primary" strokeWidth={1.5} />
                <div className="font-medium">Upload image</div>
                <div className="text-xs text-muted-foreground">JPG, PNG, HEIC up to 10MB</div>
              </button>
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-3">
              <img src={preview} alt="receipt" className="h-48 w-full rounded-lg border border-border object-cover" />
              <div className="space-y-2 text-sm">
                {busy && (
                  <div className="flex items-center gap-2 text-muted-foreground">
                    <Loader2 className="h-4 w-4 animate-spin" /> Reading receipt…
                  </div>
                )}
                {data && (
                  <>
                    <div className="text-xs uppercase tracking-widest text-muted-foreground">Merchant</div>
                    <div className="font-display text-lg font-medium">{data.merchant || "—"}</div>
                    {data.items?.length > 0 && (
                      <div className="max-h-32 overflow-y-auto rounded border border-border p-2 text-xs">
                        {data.items.slice(0, 8).map((it, i) => (
                          <div key={i} className="flex justify-between"><span className="truncate">{it.name}</span><span>{Number(it.price || 0).toFixed(2)}</span></div>
                        ))}
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>
          )}

          <input
            ref={fileRef}
            type="file"
            accept="image/*"
            className="hidden"
            onChange={(e) => onPick(e.target.files?.[0])}
            data-testid="receipt-file-input"
          />
          <input
            ref={cameraRef}
            type="file"
            accept="image/*"
            capture="environment"
            className="hidden"
            onChange={(e) => onPick(e.target.files?.[0])}
            data-testid="receipt-camera-input"
          />

          {data && (
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>Amount</Label>
                <Input type="number" step="0.01" value={data.amount || ""} onChange={(e) => update("amount", e.target.value)} data-testid="receipt-amount-input" />
              </div>
              <div className="space-y-1.5">
                <Label>Category</Label>
                <Input value={data.category || ""} onChange={(e) => update("category", e.target.value)} />
              </div>
              <div className="space-y-1.5">
                <Label>Date</Label>
                <Input type="date" value={data.date || new Date().toISOString().slice(0, 10)} onChange={(e) => update("date", e.target.value)} />
              </div>
              <div className="space-y-1.5">
                <Label>Payment</Label>
                <Input value={data.payment_method || ""} onChange={(e) => update("payment_method", e.target.value)} />
              </div>
            </div>
          )}
        </div>

        <DialogFooter className="gap-2">
          {preview && !busy && (
            <Button variant="outline" type="button" onClick={() => fileRef.current?.click()}>
              <Upload className="h-4 w-4" /> Replace
            </Button>
          )}
          <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button
            type="button"
            onClick={save}
            disabled={!data || !data.amount}
            className="rounded-full bg-primary text-primary-foreground hover:bg-primary/90"
            data-testid="receipt-save-button"
          >
            <CheckCircle2 className="h-4 w-4" /> Save expense
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
